import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  useTheme,
  Paper,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FeedbackIcon from "@mui/icons-material/Feedback";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Redux
import { useRedux } from "../../../hooks";
import {
  submitStepConfig,
  submitFeedbackThunk,
  deleteStepThunk,
  setSelectedOfficeType,
  openStepDetailModal,
  closeStepDetailModal,
  openFeedbackModal,
  closeFeedbackModal,
  openCanvasViewModal,
  closeCanvasViewModal,
} from "../../../redux/process/actions";

// Types
import {
  OfficeType,
  WorkflowCategoryData,
  WorkflowProcessData,
  WorkflowStepData,
  FeedbackEntry,
  DeleteStepPayload,
} from "../../../types/process";

// Components
import StepDetailModal from "./StepDetailModal";
import FeedbackModal from "./FeedbackModal";
import WorkflowCanvasViewModal from "./WorkflowCanvasViewModal";
import WorkflowStepItem from "./WorkflowStepItem";
import ProcessProgress from "./ProcessProgress";
import ProcessSkeleton from "./ProcessSkeleton";
import EmptyWorkflowState from "./EmptyWorkflowState";
import LanguageSelector from "../../../components/LanguageSelector";

// Hooks
import { useWorkflowData } from "../../../hooks/useWorkflowData";

const ProcessIndex: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { dispatch, useAppSelector } = useRedux();

  // Redux state
  const { categories, loading, error } = useAppSelector(
    (state: any) =>
      state.Process?.workflows || {
        categories: [],
        loading: false,
        error: null,
      },
  );

  const {
    selectedOfficeType,
    selectedStep,
    isStepDetailModalOpen,
    feedbackTarget,
    isFeedbackModalOpen,
    isCanvasViewOpen,
    canvasViewCategory,
  } = useAppSelector(
    (state: any) =>
      state.Process?.processUi || {
        selectedOfficeType: OfficeType.ALL,
        selectedStep: null,
        isStepDetailModalOpen: false,
        feedbackTarget: null,
        isFeedbackModalOpen: false,
        isCanvasViewOpen: false,
        canvasViewCategory: null,
      },
  );

  const [currentStepContext, setCurrentStepContext] = useState<{
    categoryId: string | null;
    processId: string | null;
  }>({ categoryId: null, processId: null });

  const [selectedTab, setSelectedTab] = useState(0);

  // Initialize workflow data polling
  useWorkflowData();

  const handleStepSelect = useCallback(
    (step: WorkflowStepData, categoryId: string, processId: string) => {
      setCurrentStepContext({ categoryId, processId });
      dispatch(openStepDetailModal(step));
    },
    [dispatch],
  );

  const handleFeedbackSelect = useCallback(
    (target: {
      type: "category" | "step";
      id: string;
      name: string;
      feedbackHistory?: FeedbackEntry[];
    }) => {
      dispatch(openFeedbackModal(target));
    },
    [dispatch],
  );

  const handleStepDelete = useCallback(
    (payload: DeleteStepPayload) => {
      dispatch(deleteStepThunk(payload));
    },
    [dispatch],
  );

  const handleViewAsDiagram = useCallback(
    (category: WorkflowCategoryData) => {
      dispatch(openCanvasViewModal(category));
    },
    [dispatch],
  );

  const handleCanvasStepNodeClick = useCallback(
    (step: WorkflowStepData, categoryId: string, processId: string) => {
      handleStepSelect(step, categoryId, processId);
    },
    [handleStepSelect],
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const officeTypes = [
      OfficeType.ALL,
      OfficeType.FRONT_OFFICE,
      OfficeType.BACK_OFFICE,
    ];
    dispatch(setSelectedOfficeType(officeTypes[newValue]));
  };

  const filteredCategories = (categories || []).filter(
    (category: WorkflowCategoryData) =>
      selectedOfficeType === OfficeType.ALL ||
      category.type === selectedOfficeType,
  );

  if (loading && (!categories || categories.length === 0)) {
    return <ProcessSkeleton />;
  }

  return (
    <>
      <Box
        sx={{
          height: "100vh",
          backgroundColor: "#f8f9fa",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            p: { xs: 1, sm: 2 },
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 3, position: "relative" }}>
            {/* Language Selector positioned in header */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: 0,
                zIndex: 10,
              }}
            >
              <LanguageSelector />
            </Box>

            <Typography
              variant="h4"
              component="h1"
              sx={{
                mb: 2,
                fontWeight: "bold",
                color: "#1a1a1a",
                fontSize: { xs: "1.75rem", sm: "2.125rem" },
                pr: { xs: 6, sm: 8 },
              }}
            >
              {t("workflow_dashboard")}
            </Typography>

            {/* Office Type Tabs */}
            <Paper
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                overflow: "hidden",
              }}
            >
              <Tabs
                value={selectedTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                sx={{
                  "& .MuiTab-root": {
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    px: { xs: 1, sm: 2 },
                    minHeight: { xs: 40, sm: 48 },
                  },
                }}
              >
                <Tab label={t("all")} />
                <Tab
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t("front_office")}
                      <Tooltip
                        title={
                          t("front_office_description") ||
                          "Customer-facing processes and services"
                        }
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </Tooltip>
                    </Box>
                  }
                />
                <Tab
                  label={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      {t("back_office")}
                      <Tooltip
                        title={
                          t("back_office_description") ||
                          "Internal processes and support functions"
                        }
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                      </Tooltip>
                    </Box>
                  }
                />
              </Tabs>
            </Paper>

            {/* Jess Info Alert */}
            <Alert
              severity="info"
              sx={{
                mb: 3,
                backgroundColor: "rgba(255,255,255,0.95)",
                backdropFilter: "blur(10px)",
                borderRadius: 2,
                boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                color: theme.palette.text.primary,
                "& .MuiAlert-icon": {
                  color: "#2575fc",
                },
              }}
            >
              {t("jess_info_text")}
            </Alert>
          </Box>

          {/* Error Display */}
          {error && !loading && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                {t("error_occurred")}
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {/* Empty State */}
          {!loading && !error && filteredCategories.length === 0 && (
            <EmptyWorkflowState />
          )}

          {/* Workflow Categories */}
          {filteredCategories.map((category: WorkflowCategoryData) => (
            <Card
              key={category.id}
              sx={{
                mb: { xs: 2, sm: 3 },
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: { xs: "none", sm: "translateY(-2px)" },
                  boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                },
              }}
            >
              <Accordion
                defaultExpanded
                TransitionProps={{
                  timeout: 400,
                  style: {
                    transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{ color: "#ffffff", transition: "transform 0.3s" }}
                    />
                  }
                  sx={{
                    background:
                      "radial-gradient(circle at top right,#7956f7 0,#0000 60%) no-repeat,radial-gradient(circle at bottom left,#7956f7 0,#0000 60%) no-repeat,linear-gradient(145deg,#36276f,#4c3699 50%,#36276f)",
                    color: "#ffffff",
                    "& .MuiTypography-root": {
                      color: "#ffffff",
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                    "&:hover": {
                      filter: "brightness(1.1)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "flex-start", sm: "center" },
                      justifyContent: "space-between",
                      width: "100%",
                      pr: 2,
                      gap: { xs: 1, sm: 0 },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {category.name}
                        </Typography>
                        {category.hasFeedback && (
                          <FeedbackIcon
                            sx={{ fontSize: 20, color: "#ffd89b" }}
                          />
                        )}
                      </Box>
                      <ProcessProgress category={category} />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignSelf: { xs: "flex-end", sm: "center" },
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      {/* Diagram button hidden for now */}
                      {/*<Button
                    size="small"
                    variant="outlined"
                    sx={{ 
                      color: theme.palette.primary.contrastText,
                      borderColor: theme.palette.primary.contrastText,
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        borderColor: theme.palette.primary.contrastText,
                      }
                    }}
                    onClick={() => handleViewAsDiagram(category)}
                  >
                    {t('diagram')}
                  </Button>*/}
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<FeedbackIcon />}
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "#ffffff",
                          backdropFilter: "blur(10px)",
                          borderRadius: 2,
                          textTransform: "none",
                          "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.3)",
                          },
                        }}
                        onClick={() =>
                          handleFeedbackSelect({
                            type: "category",
                            id: category.id,
                            name: category.name,
                            feedbackHistory: category.feedbackHistory,
                          })
                        }
                      >
                        {t("feedback.")}
                      </Button>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 3, backgroundColor: "#f8f9fa" }}>
                  {category.processes.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{ fontStyle: "italic", color: "text.secondary" }}
                    >
                      {t("no_processes_in_category")}
                    </Typography>
                  ) : (
                    category.processes.map((process: WorkflowProcessData) => (
                      <Card
                        key={process.id}
                        sx={{
                          mb: 2,
                          backgroundColor: "#ffffff",
                          borderRadius: 2,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                          transition: "box-shadow 0.2s ease",
                          "&:hover": {
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          },
                        }}
                      >
                        <CardContent>
                          {process.steps.length === 0 ? (
                            <Typography
                              variant="body2"
                              sx={{
                                fontStyle: "italic",
                                color: "text.secondary",
                              }}
                            >
                              {t("no_steps_in_process")}
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {process.steps.map(step => (
                                <WorkflowStepItem
                                  key={step.id}
                                  step={step}
                                  categoryId={category.id}
                                  processId={process.id}
                                  onStepClick={() =>
                                    handleStepSelect(
                                      step,
                                      category.id,
                                      process.id,
                                    )
                                  }
                                  onFeedbackClick={() =>
                                    handleFeedbackSelect({
                                      type: "step",
                                      id: step.id,
                                      name: step.name,
                                      feedbackHistory: step.feedbackHistory,
                                    })
                                  }
                                  onDeleteClick={handleStepDelete}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </AccordionDetails>
              </Accordion>
            </Card>
          ))}

          {/* Modals */}
          <StepDetailModal
            isOpen={isStepDetailModalOpen}
            onClose={() => dispatch(closeStepDetailModal())}
            step={selectedStep}
            categoryId={currentStepContext.categoryId}
            processId={currentStepContext.processId}
          />

          <FeedbackModal
            isOpen={isFeedbackModalOpen}
            onClose={() => dispatch(closeFeedbackModal())}
            target={feedbackTarget}
          />

          <WorkflowCanvasViewModal
            isOpen={isCanvasViewOpen}
            onClose={() => dispatch(closeCanvasViewModal())}
            category={canvasViewCategory}
            onStepNodeClick={handleCanvasStepNodeClick}
          />

          {/* Loading indicator for updates */}
          {loading && categories && categories.length > 0 && (
            <Box
              sx={{
                position: "fixed",
                bottom: 16,
                right: 16,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                px: 2,
                py: 1,
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <CircularProgress size={20} sx={{ color: "inherit" }} />
              <Typography variant="body2">{t("updating")}...</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ProcessIndex;
